import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {RuleActions} from './rule.actions';
import {AppActions} from '../../../core/store/app.actions';
import {catchError, filter, from, map, mergeMap, of, switchMap, take, tap, withLatestFrom} from 'rxjs';
import {RULE_STATE_SERVICE_INJECTION_TOKEN, RuleStateService} from '../../../core/services/api/rule-state-service.interface';
import {Store} from '@ngrx/store';
import {selectRuleById, selectRuleFilter} from './rule.selectors';
import {ApiCreateRuleRequestDto, ApiRuleResponseDto, ApiUpdateRuleRequestDto} from '@birco/rainmesh-api';
import {RuleConverter} from '../converter/rule.converter';

@Injectable()
export class RuleEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private ruleStateService = inject<RuleStateService>(RULE_STATE_SERVICE_INJECTION_TOKEN);


  public updateRuleFilter$ = createEffect(() => this.actions$.pipe(
    ofType(RuleActions.public.updateRuleFilter),
    map((_props) => RuleActions.private.loadRulesWithFilters())
  ));

  public loadRulesWithFilters$ = createEffect(() => this.actions$.pipe(
      ofType(RuleActions.private.loadRulesWithFilters),
      withLatestFrom(this.store.select(selectRuleFilter)),
      mergeMap(([_action, filter]) => this.ruleStateService.getRules(filter).pipe(
        map((rulesResponse) => RuleActions.private.loadRulesSuccess({rulesResponseDto: rulesResponse})),
        catchError((error) => of(RuleActions.private.loadRulesFailure({error}))),
      )),
    ),
    {dispatch: true}
  );

  public createOrUpdateRule$ = createEffect(() => this.actions$.pipe(
      ofType(RuleActions.public.createOrUpdateRule),
      map((props) => {
        return !props.id
          ? RuleActions.private.createRule({
            requestDto: RuleConverter.toRequestDto(props.requestDto) as ApiCreateRuleRequestDto
          })
          : RuleActions.private.updateRuleWithId({
            id: props.id,
            requestDto: RuleConverter.toRequestDto(props.requestDto) as ApiUpdateRuleRequestDto
          });
      }),
    ),
    {dispatch: true}
  );

  public createRule$ = createEffect(() => this.actions$.pipe(
      ofType(RuleActions.private.createRule),
      switchMap((props) => this.ruleStateService.createRule({
        requestDto: props.requestDto
      }).pipe(
        map((responseDto: ApiRuleResponseDto) => RuleActions.private.createRuleSuccess({ruleResponseDto: responseDto})),
        catchError((error) => of(RuleActions.private.createRuleFailure({error}))),
      )),
    ),
    {dispatch: true}
  );

  public createRuleSuccess$ = createEffect(() => this.actions$.pipe(
      ofType(RuleActions.private.createRuleSuccess),
      mergeMap((props) =>
        from([
          RuleActions.private.loadRulesWithFilters(),
          AppActions.public.navigateToRuleWithId({
            id: props.ruleResponseDto.id
          }),
        ]))
    ),
    {dispatch: true}
  );

  public updateRuleWithId$ = createEffect(() => this.actions$.pipe(
      ofType(RuleActions.private.updateRuleWithId),
      switchMap((props) => this.ruleStateService.updateRule({
        ruleId: props.id,
        requestDto: props.requestDto
      }).pipe(
        map((responseDto: ApiRuleResponseDto) => RuleActions.private.updateRuleSuccess({ruleResponseDto: responseDto})),
        catchError((error) => of(RuleActions.private.updateRuleFailure({
          id: 'TODO',
          error: error,
        }))),
      )),
    ),
    {dispatch: true}
  );

  public updateRuleSuccess$ = createEffect(() => this.actions$.pipe(
      ofType(RuleActions.private.updateRuleSuccess),
      tap((_props) => this.store.dispatch(RuleActions.private.loadRulesWithFilters())),
      map((props) => AppActions.public.navigateToRuleWithId({
          id: props.ruleResponseDto.id
        }),
      ),
    ),
    {dispatch: true}
  );

  public deleteRuleById$ = createEffect(() => this.actions$.pipe(
      ofType(RuleActions.public.deleteRuleById),
      switchMap((props) =>
        this.ruleStateService.deleteRule({
          ruleId: props.id
        }).pipe(
          mergeMap(() => from([
            RuleActions.private.deleteRuleSuccess({
              id: props.id
            }),
            AppActions.public.navigateToRules(),
          ])),
          catchError((error) => of(RuleActions.private.deleteRuleFailure({
            id: 'TODO',
            error: error,
          }))),
        )
      ),
    ),
    {dispatch: true}
  );

  public deleteRuleSuccess$ = createEffect(() => this.actions$.pipe(
      ofType(RuleActions.private.deleteRuleSuccess),
      map((_props) => RuleActions.private.loadRulesWithFilters()),
    ),
    {dispatch: true}
  );

  public loadRuleWithId$ = createEffect(() => this.actions$.pipe(
      ofType(RuleActions.public.loadRuleWithId),
      switchMap((props) =>
        this.store.select(selectRuleById(props.id)).pipe(
          take(1),
          map((existingRule) => ({...props, existingRule})),
        )
      ),
      filter((props) => {
        const filterResult = !props.skipIfInStore || !props.existingRule;
        if (!filterResult && props.existingRule) {
          this.store.dispatch(RuleActions.private.skipLoadRuleWithExisting());
        }
        return filterResult;
      }),
      mergeMap((props) => this.ruleStateService.getRuleById({
        id: props.id
      }).pipe(
        map((ruleResponse) => RuleActions.private.loadRuleSuccess({ruleResponseDto: ruleResponse})),
        catchError((error) => of(RuleActions.private.loadRuleFailure({error}))),
      )),
    ),
    {dispatch: true}
  );

  public updateIsActive$ = createEffect(() => this.actions$.pipe(
      ofType(RuleActions.public.updateIsActive),
      switchMap((props) => this.ruleStateService.updateIsActive({
        id: props.id,
        isActive: props.isActive,
      }).pipe(
        map((ruleResponse) => RuleActions.private.updateIsActiveSuccess({
          responseDto: ruleResponse,
        })),
        catchError((e) => of(RuleActions.private.updateIsActiveFailure({
          id: 'TODO',
          error: e,
        }))),
      )),
    ),
    {dispatch: true},
  );

  public loadRulesWithProvidedFilters$ = createEffect(() => this.actions$.pipe(
      ofType(RuleActions.private.loadRulesWithProvidedFilters),
      switchMap((props) => this.ruleStateService.getRules(
        props.filter,
      ).pipe(
        map((responseDto) => RuleActions.private.loadRulesWithProvidedFiltersSuccess({
          responseDto: responseDto,
        })),
        catchError((error) => of(RuleActions.private.loadRulesWithProvidedFiltersFailure({
          error: error,
        }))),
      )),
    )
  );

  public loadAllRulesByFilters$ = createEffect(() => this.actions$.pipe(
      ofType(RuleActions.public.loadAllRulesByFilters),
      map((props) => RuleActions.private.loadNextOfAll({page: 0, filter: props.filter ?? {}})),
      catchError((error) => of(RuleActions.private.loadAllRulesByFiltersFailure({
        error: error,
      }))),
    )
  );

  public loadNextOfAll$ = createEffect(() => this.actions$.pipe(
      ofType(RuleActions.private.loadNextOfAll),
      switchMap((props) => {
          const pageSize = 50;
          return this.ruleStateService.getRules(
            {...props.filter, pageSize: pageSize, page: props.page},
          ).pipe(
            map((responseDto) => {
              this.store.dispatch(RuleActions.private.loadNextOfAllSuccess({
                rulesResponseDto: responseDto,
              }))

              if ((props.page + 1) * pageSize >= responseDto.totalCount) {
                return RuleActions.private.loadAllRulesByFiltersSuccess()
              }

              return RuleActions.private.loadNextOfAll({page: props.page + 1, filter: props.filter})
            }),
            catchError((error) => of(
              RuleActions.private.loadNextOfAllFailure({error}),
              RuleActions.private.loadAllRulesByFiltersFailure({error})
            )),
          )
        }
      ),
    )
  );
}
